'use client';

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Bug, Loader2, ImageIcon, X, Send } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/label';
import { submitBugReport } from '@/lib/public-api';
import { useTranslations } from '@/i18n/I18nProvider';

/**
 * Capture a screenshot of the current page using modern-screenshot.
 */
async function captureScreenshot(): Promise<Blob | null> {
    try {
        const { domToBlob } = await import('modern-screenshot');

        // Hide the floating bug report widget during capture
        const bugWidget = document.querySelector('[data-bug-report-widget]') as HTMLElement | null;
        if (bugWidget) {
            bugWidget.style.visibility = 'hidden';
        }

        // Use lower scale on mobile to reduce file size
        const isMobile = window.innerWidth < 768;
        const scale = isMobile ? 1 : Math.min(window.devicePixelRatio, 2);

        const blob = await domToBlob(document.documentElement, {
            scale,
            quality: isMobile ? 0.8 : 0.92,
            type: 'image/jpeg',
            width: window.innerWidth,
            height: window.innerHeight,
            style: {
                transform: 'none',
                transformOrigin: 'top left',
            },
            filter: (node: Node) => {
                if (node instanceof Element) {
                    if (node.hasAttribute('data-radix-portal')) return false;
                    if (node.hasAttribute('data-radix-popper-content-wrapper')) return false;
                    if (node.getAttribute('role') === 'dialog') return false;
                    if (node.classList.contains('DialogOverlay') || node.getAttribute('data-overlay') === 'true')
                        return false;
                    if (node.classList.contains('fixed') && node.classList.contains('inset-0')) return false;
                }
                return true;
            },
        });

        // Restore the widget visibility
        if (bugWidget) {
            bugWidget.style.visibility = 'visible';
        }

        return blob;
    } catch (error) {
        console.error('Screenshot capture failed:', error);
        return null;
    }
}

export function BugReportWidget() {
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [screenshot, setScreenshot] = useState<Blob | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [description, setDescription] = useState('');
    const [includeScreenshot, setIncludeScreenshot] = useState(true);
    const [showPreview, setShowPreview] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslations('bugReport');

    const handleOpenBugReport = useCallback(async () => {
        if (isOpen) {
            handleClose();
            return;
        }

        setIsCapturing(true);
        try {
            const captured = await captureScreenshot();
            setScreenshot(captured);
        } catch (error) {
            console.error('Failed to capture screenshot:', error);
            setScreenshot(null);
        } finally {
            setIsCapturing(false);
            setIsOpen(true);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsExpanded(true);
                });
            });
        }
    }, [isOpen]);

    const handleClose = useCallback(() => {
        setIsExpanded(false);
        // Wait for animation to complete before fully closing
        setTimeout(() => {
            setIsOpen(false);
            setScreenshot(null);
            setDescription('');
            setShowPreview(false);
        }, 150);
    }, []);

    // Focus textarea when panel opens
    useEffect(() => {
        if (isExpanded && textareaRef.current) {
            setTimeout(() => textareaRef.current?.focus(), 150);
        }
    }, [isExpanded]);

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (isOpen && panelRef.current && !panelRef.current.contains(e.target as Node)) {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, handleClose]);

    // Handle Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                handleClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, handleClose]);

    // Create object URL for preview
    const screenshotPreviewUrl = useMemo(() => {
        if (screenshot) {
            return URL.createObjectURL(screenshot);
        }
        return null;
    }, [screenshot]);

    // Cleanup object URL on unmount or when screenshot changes
    useEffect(() => {
        return () => {
            if (screenshotPreviewUrl) {
                URL.revokeObjectURL(screenshotPreviewUrl);
            }
        };
    }, [screenshotPreviewUrl]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!description.trim()) {
            return;
        }

        setIsSubmitting(true);

        const success = await submitBugReport({
            description,
            screenshot: includeScreenshot ? screenshot : null,
        });

        setIsSubmitting(false);

        if (success) {
            handleClose();
        }
    };

    return (
        <div
            data-bug-report-widget
            className="hidden sm:block fixed bottom-6 right-6 z-50"
            ref={panelRef}
        >
            {/* Expandable Panel */}
            <div
                className={`
                    absolute bottom-0 right-0 origin-bottom-right
                    bg-white dark:bg-[#0f0f14] border border-gray-200 dark:border-white/10
                    rounded-2xl shadow-2xl overflow-hidden
                    transition-all duration-150 ease-out
                    ${isOpen
                        ? isExpanded
                            ? 'w-[380px] opacity-100 scale-100'
                            : 'w-14 h-14 opacity-100 scale-100'
                        : 'w-14 h-14 opacity-0 scale-75 pointer-events-none'
                    }
                `}
            >
                {isOpen && (
                    <div
                        className={`
                            flex flex-col max-h-[500px]
                            transition-opacity duration-100
                            ${isExpanded ? 'opacity-100 delay-75' : 'opacity-0'}
                        `}
                    >
                        {/* Header */}
                        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/5">
                            <div>
                                <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                                    {t('reportBug')}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-white/50 mt-0.5">
                                    {t('helpImprove')}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                aria-label="Close"
                            >
                                <X className="h-4 w-4 text-gray-500 dark:text-white/50" />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <form id="bug-report-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto overscroll-contain p-4 pb-2">
                            <div className="space-y-3">
                                {/* Textarea */}
                                <div className="space-y-1.5">
                                    <Label htmlFor="description" className="text-xs">
                                        {t('whatHappened')}
                                    </Label>
                                    <textarea
                                        ref={textareaRef}
                                        id="description"
                                        placeholder={t('describePlaceholder')}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className="w-full min-h-[100px] px-3 py-2 text-sm border border-gray-200 dark:border-white/10 rounded-lg bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple/30 dark:focus:ring-purple-bright/30 focus:border-purple dark:focus:border-purple-bright/50 resize-none transition-colors"
                                        maxLength={5000}
                                        required
                                    />
                                    <p className="text-[10px] text-gray-400 dark:text-white/40">
                                        {t('characters', { count: description.length })}
                                    </p>
                                </div>

                                {/* Screenshot toggle */}
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="screenshot"
                                            checked={includeScreenshot}
                                            onChange={(e) => setIncludeScreenshot(e.target.checked)}
                                            className="h-3.5 w-3.5 rounded border-gray-300 dark:border-white/20 text-purple dark:text-purple-bright focus:ring-purple/50 dark:focus:ring-purple-bright/50 bg-white dark:bg-white/5"
                                        />
                                        <Label htmlFor="screenshot" className="cursor-pointer text-xs">
                                            {t('includeScreenshot')}
                                        </Label>
                                    </div>

                                    {/* Screenshot preview */}
                                    {screenshot && includeScreenshot && (
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => setShowPreview(!showPreview)}
                                                className="flex items-center gap-1.5 text-xs text-purple dark:text-purple-bright hover:underline"
                                            >
                                                <ImageIcon className="h-3 w-3" />
                                                {showPreview ? t('hidePreview') : t('showPreview')}
                                            </button>

                                            {showPreview && screenshotPreviewUrl && (
                                                <div className="mt-2 relative rounded-md border border-gray-200 dark:border-white/10 overflow-hidden">
                                                    <img
                                                        src={screenshotPreviewUrl}
                                                        alt={t('screenshotPreview')}
                                                        className="w-full h-auto max-h-[100px] object-contain bg-gray-100 dark:bg-gray-900"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowPreview(false)}
                                                        className="absolute top-1 right-1 p-0.5 rounded-full bg-black/50 text-white hover:bg-black/70"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!screenshot && includeScreenshot && (
                                        <p className="text-[10px] text-yellow-600 dark:text-yellow-400">
                                            {t('screenshotFailed')}
                                        </p>
                                    )}
                                </div>

                                <p className="text-[10px] text-gray-400 dark:text-white/40">
                                    {t('autoCollect')}
                                </p>
                            </div>
                        </form>

                        {/* Submit button - sticky footer */}
                        <div className="flex-shrink-0 p-4 pt-3 border-t border-gray-100 dark:border-white/5 bg-white dark:bg-[#0f0f14]">
                            <Button
                                type="submit"
                                form="bug-report-form"
                                className="w-full h-9 text-sm"
                                disabled={isSubmitting || !description.trim()}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                        {t('submitting')}
                                    </>
                                ) : (
                                    <>
                                        <Send className="mr-2 h-3.5 w-3.5" />
                                        {t('submitReport')}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Floating Bug Button */}
            <Button
                variant="default"
                size="icon"
                className={`
                    relative h-14 w-14 rounded-full shadow-lg
                    transition-all duration-150 ease-out
                    ${isOpen && isExpanded
                        ? 'opacity-0 scale-75 pointer-events-none'
                        : 'opacity-100 scale-100 hover:scale-110'
                    }
                `}
                onClick={handleOpenBugReport}
                disabled={isCapturing}
                aria-label={t('reportBug')}
            >
                {isCapturing ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                    <Bug className="h-6 w-6" />
                )}
            </Button>
        </div>
    );
}
